import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X, ArrowUpRight } from "@phosphor-icons/react";
import { SITE, NAV_LINKS } from "@/data/content";
import { useSmoothScroll } from "./useSmoothScroll";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const scrollTo = useSmoothScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 ${
        scrolled ? "glass border-b border-white/[0.08]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[88rem] mx-auto flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("#home")}
          className="flex flex-col items-start leading-none"
        >
          <span className="font-display text-lg md:text-xl font-bold tracking-[0.15em] text-zinc-50 hover:text-gold transition-colors duration-300">
            {SITE.name}
          </span>
          <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.35em] text-gold/80">by Inder</span>
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => scrollTo(l.href)}
              className="text-sm text-zinc-400 hover:text-zinc-50 transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={() => scrollTo("#contact", '[data-testid="contact-name-input"]')}
            className="hidden lg:inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-gold px-6 py-3 text-sm font-semibold text-void hover:bg-gold-soft transition-colors duration-300"
          >
            Let&apos;s Work Together
            <ArrowUpRight size={16} weight="bold" />
          </button>
          <button
            data-testid="nav-menu-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-zinc-100 p-2"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden glass border-b border-white/[0.08] overflow-hidden"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => { setOpen(false); scrollTo(l.href); }}
                  className="text-left font-display text-2xl text-zinc-200 hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => { setOpen(false); scrollTo("#contact", '[data-testid="contact-name-input"]'); }}
                className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-void"
              >
                Let&apos;s Work Together
                <ArrowUpRight size={16} weight="bold" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
